/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Popover,
  List,
  ListItem,
  Tooltip,
  makeStyles,
  Button,
} from "@material-ui/core";
import { AiFillCloseCircle } from "react-icons/ai";
import { FiChevronDown } from "react-icons/fi";
import _ from "lodash";
import FiltersSearchBoxComponent from "./FiltersSearchBoxComponent";

const useStyles = makeStyles((theme) => ({
  padding: {
    padding: theme.spacing(1.5),
  },
  small: {
    maxHeight: "35px",
    padding: "8px",
  },
  smallFont: {
    fontSize: "12px",
  },
  filterContainer: {
    display: "flex",
    alignItems: "center",
    boxShadow: "0 1px 4px 0 rgba(37, 50, 56, 0.1)",
    borderRadius: "5px",
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#ECEFF1",
    },
  },
  inputTitleBox: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
  },
  alignCenterFlex: {
    display: "flex",
    alignItems: "center",
  },
  popoverRoot: {
    marginTop: "-5px",
    height: "550px",
  },
  listRoot: {
    padding: "0px",
  },
  listItem: {
    fontSize: "16px",
    minWidth: "300px",
    padding: "20px",
    "&::before": {
      content: `""`,
      position: "absolute",
      width: "calc(100% - 30px)",
      height: "1px",
      backgroundColor: "#ECEFF1",
      top: "0",
      left: "15px",
    },
    "&:hover": {
      backgroundColor: "#ECEFF1",
      cursor: "pointer",
    },
  },
  nested: {
    paddingLeft: "40px",
  },
  groupHeader: {
    padding: "16px",
    "&:hover": {
      backgroundColor: "#ECEFF1",
      cursor: "pointer",
    },
  },
  listGroup: {
    borderTop: "1px solid #ECEFF1",
    padding: "0px",
  },
  filterTitleAndIcon: {
    display: "flex",
    alignItems: "center",
    width: "calc(100% - 40px)",
    "& >:last-child": {
      marginLeft: "10px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      width: "calc(100% - 30px)",
      display: "inline-block",
    },
  },
  popoverTitle: {
    boxSizing: "border-box",
    display: "flex",
    padding: "20px",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    backgroundColor: "#fff",
    zIndex: 2,
    width: "100%",
    borderBottom: "1px solid #ECEFF1",
  },
  popoverTitleLabel: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  withFilterApplied: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: "#C45852",
      cursor: "pointer",
    },
  },
  dropdownRightIcon: {
    // marginLeft: "15px",
  },
  disabledSelect: {
    backgroundColor: "#f0f0f0",
    color: "#b0b0b0",
    "&:hover": {
      cursor: "not-allowed",
      backgroundColor: "#f0f0f0",
    },
  },
  popoverCloseButton: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  popoverSearchBoxContainer: {
    padding: "20px",
    marginBottom: "20px",
    position: "sticky",
    top: 77,
    backgroundColor: "#fff",
    zIndex: 2,
    width: "100%",
    borderBottom: "1px solid #ECEFF1",
  },
  popoverSearchBoxSmall: {
    top: 74,
  },
  listItemLabel: {
    userSelect: "none",
    pointerEvents: "none",
  },
}));

const FiltersSelectComponent = ({
  inputData,
  disabled,
  required,
  filters,
  small,
  sort,
  handleChange,
  view = "grid",
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Map());
  const [searchTerm, setSearchTerm] = useState("");

  const handleFilterclick = (event) => {
    if (disabled) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClickOnItem = (item, event) => {
    if (selectedItems.has(item.id)) {
      if (selectedItems.delete(item.id)) {
        setSelectedItems(selectedItems);
        event.target.classList.remove(classes.withFilterApplied);
      }
    } else {
      setSelectedItems(selectedItems.set(item.id, item));
      event.target.classList.add(classes.withFilterApplied);
    }
  };

  const handleListItemClick = (event, item) => {
    event.stopPropagation();
    handleClickOnItem(item, event);
  };

  const handleRemoveFilterClick = (event) => {
    if (event) event.stopPropagation();
    setSelectedItems(new Map());
    handleChange({ [inputData.key]: "" });
    setAnchorEl(null);
  };

  const handleOnClose = () => {
    const filtersChanged =
      JSON.stringify(filters[inputData.key] || []) !==
      JSON.stringify(Array.from(selectedItems.values()));
    if (filtersChanged) {
      if (Array.from(selectedItems.values()).length > 0) {
        handleChange({ [inputData.key]: Array.from(selectedItems.values()) });
      } else {
        handleChange({ [inputData.key]: "" });
      }
    }
    setAnchorEl(null);
  };

  const getFilterDataBySearch = (filterData = [], searchStr = "") => {
    if (!Array.isArray(filterData)) return [];
    return Array.from(filterData).filter((item) =>
      String(item?.label).includes(searchStr)
    );
  };

  const handleCycleItemClick = (event, item) => {
    const filtersChanged =
      JSON.stringify(filters[inputData.key] || []) !==
      JSON.stringify(Array.from(selectedItems.values()));
    // Prevent event bubbling
    event.stopPropagation();

    // For cycle filter, we want to select only this item

    setSelectedItems(selectedItems.set(item.id, item));
    event.target.classList.add(classes.withFilterApplied);
    if (filtersChanged) {
      handleChange({ [inputData.key]: item });
    }

    // Close the popover immediately after selection
    setAnchorEl(null);
  };

  // Special handler for when the cycle dropdown is closed by clicking outside
  const handleCycleClose = () => {
    // Don't do anything special, just close the dropdown
    // without selecting anything
    setAnchorEl(null);
  };

  // Auto set selected items when the component mounts (needed when changing between summary and grid views)
  useEffect(() => {
    const currentFilters = filters[inputData.key];
    if (currentFilters && view === "summary") {
      currentFilters.forEach((item) => {
        setSelectedItems(selectedItems.set(item.id, item));
      });
    }
  }, []);

  // Reset product filter in certain conditions
  useEffect(() => {
    if (
      inputData.key !== "product" ||
      window.location.pathname.includes("competitors")
    )
      return;
    handleRemoveFilterClick();
  }, [filters.brand, filters.category, filters.line]);

  // Reset retailer filter in certain conditions
  useEffect(() => {
    if (inputData.key === "retailers" && view === "summary") {
      handleRemoveFilterClick();
    }
  }, [filters.category, filters.product]);

  const filterData = getFilterDataBySearch(inputData.data, searchTerm);

  return (
    <>
      <div
        role="presentation"
        onClick={handleFilterclick}
        className={`${classes.filterContainer} ${classes.padding} ${filters[
          inputData.key
        ] && classes.withFilterApplied} ${disabled &&
          classes.disabledSelect} ${small && classes.small} ${small &&
          classes.smallFont} `}
      >
        {/* The tooltip */}
        <Tooltip
          title={inputData.label}
          placement="right"
          data-testid="filterTooltip"
        >
          {/* The select dropdown box */}
          <Box component="div" className={classes.inputTitleBox}>
            <Box component="span" className={classes.filterTitleAndIcon}>
              {inputData.icon}
              <Typography
                variant="body1"
                className={`${classes.listItemLabel} ${
                  classes.alignCenterFlex
                } ${small && classes.smallFont} `}
                data-testid="filterText"
              >
                {inputData.label}
              </Typography>
            </Box>
            {filters[inputData.key] && !required ? (
              // The X icon to delete selection
              <AiFillCloseCircle
                className={classes.dropdownRightIcon}
                color="#fff"
                size={24}
                onClick={(event) => handleRemoveFilterClick(event)}
              />
            ) : (
              // The dropdown icon
              <FiChevronDown
                className={classes.dropdownRightIcon}
                size={18}
                data-testid="arrowIcon"
              />
            )}
          </Box>
        </Tooltip>
      </div>
      <Popover
        className={classes.popoverRoot}
        open={Boolean(anchorEl)}
        onClose={inputData.key === "cycle" ? handleCycleClose : handleOnClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        data-testid="filterPopover"
      >
        <Box component="div" className={`${classes.popoverTitle}`}>
          <div className={classes.popoverTitleLabel}>
            {inputData.icon}
            <Typography
              variant="body1"
              className={`${classes.listItemLabel} ${
                classes.alignCenterFlex
              } ${small && classes.smallFont}`}
              data-testid="listTitle"
            >
              <b>{inputData.label}</b>
            </Typography>
          </div>
          {/* Only show Apply button for non-cycle filters */}
          {inputData.key !== "cycle" && (
            <Button
              className={`${classes.popoverCloseButton} ${small &&
                classes.smallFont} `}
              onClick={handleOnClose}
            >
              APPLY
            </Button>
          )}
        </Box>
        {inputData.key === "product" && (
          <div
            className={`${classes.popoverSearchBoxContainer} ${small &&
              classes.popoverSearchBoxSmall}`}
          >
            <FiltersSearchBoxComponent
              handleChange={(event) => {
                setSearchTerm(event.searchTerm?.toUpperCase());
              }}
            />
          </div>
        )}
        <List className={classes.listRoot}>
          {_.map(
            sort ? _.sortBy(inputData.data, "label", sort) : inputData.data,
            (item) => {
              if (
                inputData.key === "product" &&
                !filterData.some((i) => item.id === i.id)
              )
                return null;

              // Special handling for cycle filter
              if (inputData.key === "cycle") {
                const isCycleSelected = filters[inputData.key]?.id === item.id;

                return (
                  <ListItem
                    key={item.id}
                    className={`${classes.listItem} ${isCycleSelected &&
                      classes.withFilterApplied} ${small && classes.smallFont}`}
                    onClick={(event) => {
                      // For cycle, select only this item and close dropdown immediately
                      handleCycleItemClick(event, item);
                    }}
                  >
                    <Typography
                      className={`${classes.listItemLabel} ${small &&
                        classes.smallFont}`}
                    >
                      {item.label}
                    </Typography>
                  </ListItem>
                );
              }

              // Default multi-select behavior for other filters
              return (
                <ListItem
                  key={item.id}
                  className={`${classes.listItem} ${selectedItems.has(
                    item.id
                  ) && classes.withFilterApplied} ${small &&
                    classes.smallFont}`}
                  onClick={(event) => handleListItemClick(event, item)}
                >
                  <Typography
                    className={`${classes.listItemLabel} ${small &&
                      classes.smallFont}`}
                  >
                    {item.label}
                  </Typography>
                </ListItem>
              );
            }
          )}
        </List>
      </Popover>
    </>
  );
};

export default FiltersSelectComponent;
